import { Injectable, Injector, inject } from "@angular/core";
import { AuthService } from "../auth/auth.service";
import { OnboardingEventApiService } from "./onboarding-event-api.service";
import {
  ONBOARDING_STEPS,
  OnboardingEvent,
  OnboardingEventStatus,
  OnboardingPhase,
} from "./onboarding-event.models";

const ONCE_PREFIX = "onboarding_event_sent_";

/**
 * Reports onboarding milestones to the waba journal so support can reconstruct, from the back
 * office, what a customer actually went through — in particular the Meta Embedded Signup
 * callbacks, which Meta delivers to the browser and which used to end their life in console.log.
 *
 * Every method is fire-and-forget: nothing here returns an observable the caller has to handle,
 * and nothing here is allowed to throw into an onboarding flow.
 */
@Injectable({ providedIn: "root" })
export class OnboardingTrackerService {
  private readonly api = inject(OnboardingEventApiService);
  /**
   * AuthService is resolved on demand rather than injected: this service is pulled into flows
   * (the guide tour, the header) whose own tests never set Keycloak up, and a monitoring helper
   * must not be the reason one of them cannot be constructed.
   */
  private readonly injector = inject(Injector);

  /** Meta Embedded Signup, browser side. */
  signup(
    step: string,
    status: OnboardingEventStatus,
    label: string,
    detail?: Record<string, unknown>,
    errorMessage?: string,
  ): void {
    this.send({
      phase: "META_SIGNUP",
      step,
      status,
      label,
      errorMessage,
      detailJson: detail ? JSON.stringify(detail) : undefined,
    });
  }

  /**
   * A header setup checklist step the user completed. Sent once per browser and company: the
   * checklist recomputes its state on every page load, and the journal is a history of what
   * happened, not a heartbeat.
   */
  setupStepCompleted(stepId: string, label?: string): void {
    const step = `${ONBOARDING_STEPS.setupStepPrefix}${stepId}`;
    this.sendOnce(step, {
      phase: "SETUP_CHECKLIST",
      step,
      status: "SUCCESS",
      label: label ?? `Setup step ${stepId} completed`,
    });
  }

  /** An in-app guide the user was shown, finished or dismissed. */
  tour(tourKey: string, status: OnboardingEventStatus, label?: string): void {
    const step = `${ONBOARDING_STEPS.tourPrefix}${tourKey}`;
    this.send({
      phase: "GUIDE_TOUR",
      step,
      status,
      label: label ?? `Guide ${tourKey}`,
    });
  }

  /** Company of the signed-in user, used only to scope the "already sent" flags. */
  private currentCompanyId(): string {
    try {
      return this.injector.get(AuthService).getCompanyId() ?? "unknown";
    } catch {
      return "unknown";
    }
  }

  private sendOnce(step: string, event: Partial<OnboardingEvent>): void {
    const key = `${ONCE_PREFIX}${this.currentCompanyId()}_${step}`;
    try {
      if (localStorage.getItem(key) === "1") {
        return;
      }
      localStorage.setItem(key, "1");
    } catch {
      // Storage disabled — send anyway; a duplicate row is better than a missing one.
    }
    this.send(event);
  }

  private send(event: Partial<OnboardingEvent>): void {
    if (!event.step) {
      return;
    }
    const payload: OnboardingEvent = {
      phase: event.phase as OnboardingPhase,
      step: event.step,
      status: event.status ?? "SUCCESS",
      label: event.label,
      errorMessage: event.errorMessage,
      detailJson: event.detailJson,
      // occurredAt is left to the server: a browser clock can be minutes off, and the timeline
      // is only useful if every entry is stamped by the same clock.
    };
    try {
      this.api.track(payload).subscribe();
    } catch {
      // Never let monitoring break the flow it monitors.
    }
  }
}
